import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DashboardViewService } from 'src/app/services/dashboard.services';
import { MiniCardConfigurationComponent } from './mini-card-configuration/mini-card-configuration.component';
import { MiniCardInterface } from '../interfaces/minicard';
import { BehaviorSubject, EMPTY, Observable, Subject, catchError, filter, finalize, interval, map, merge, of, switchMap } from 'rxjs';
import { DashBoardInterface, DashBoardTypeEnum } from '../interfaces/dashboard';
import { DashboardFocusService } from 'src/app/services/dashboard-focus.service';
import { GadGetAction } from '../interfaces/gadget-events';
import { FormGroup } from '@angular/forms';
import { DateUtils } from 'src/app/system-utils/utils/DateUtils';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { openGadgetModal } from '../gadget-modal';

/*
 * Módulos NG-ZORRO usados no template: NzButtonModule, NzIconModule,
 * NzDropDownModule (todos via SharedModule).
 */
@Component({
    selector: 'mini-card',
    templateUrl: './mini-card.component.html',
    styleUrls: ['./mini-card.component.scss'],
    standalone: false
})
export class MiniCardComponent implements OnInit {

  tabName: string;
  @Input() events: Observable<FormGroup | null>;
  @Input() dashboard: DashBoardInterface;
  focus = false;
  loading = false;
  @Input() name: string;
  @Input() item: MiniCardInterface;
  @Output() deleting: EventEmitter<GadGetAction> = new EventEmitter<GadGetAction>();
  @Output() itemChange: EventEmitter<MiniCardInterface> = new EventEmitter<MiniCardInterface>();
  @Input() showConfiguration: Boolean = false;
  formFilter: FormGroup;

  icon: string;
  title: string;
  textValue: string;
  bgColor: string;
  fgColor: string;
  value: string;
  type: string;
  iconColor: string;
  isIncrease: boolean;
  isCurrency: boolean;
  duration: string;
  percentValue: string;
  refresh: number;
  query: string;
  field: string;
  lastUpdate = "01/01/1900 23:59:59";

  private readonly manualReload$ = new Subject<void>();
  private readonly refreshSeconds$ = new BehaviorSubject<number | null>(null);

  constructor(private dvs: DashboardViewService,
    private modal: NzModalService,
    private viewContainerRef: ViewContainerRef,
    public dfs: DashboardFocusService,
    private message: NzMessageService,
    private destroyRef: DestroyRef) { }

  ngOnInit(): void {

    this.tabName = this.dashboard?.tabName;
    this.loadVariables();

    this.dfs.focusChanged
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(c => this.focus = c);

    const filterTrigger$ = (this.events ?? EMPTY).pipe(
      map(form => {
        if (form != null) {
          this.formFilter = form;
        }
        return void 0;
      })
    );

    const refreshTick$ = this.refreshSeconds$.pipe(
      switchMap(seconds => (seconds != null && seconds > 0) ? interval(seconds * 1000) : EMPTY),
      filter(() => this.focus && !this.loading),
      map(() => void 0)
    );

    merge(filterTrigger$, this.manualReload$, refreshTick$)
      .pipe(
        switchMap(() => this.fetchValue$()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(row => {
        if (row !== undefined) {
          this.lastUpdate = DateUtils.getNowFormatted("DD/MM/YYYY HH:mm:ss.SSS");
          if (row == null) {
            this.value = "0";
            this.percentValue = "0";
            this.isIncrease = true;
          } else {
            this.value = row["VALUE"] as string;
            this.percentValue = row["PERCENTVALUE"] as string;
            this.isIncrease = row['INCREASE'] == 'S';
          }
        }
        this.itemChange.emit(this.item);
      });
  }

  private loadVariables(): void {
    this.icon = this.item.icon;
    this.title = this.item.title;
    this.type = this.item.type == null ? 'google' : this.item.type;
    this.textValue = this.item.textValue;
    this.value = this.item.value;

    if (this.item.iconColor != null) {
      this.iconColor = this.item.iconColor;
    }
    if (this.item.fgColor != null) {
      this.fgColor = this.item.fgColor;
    }
    if (this.item.bgColor != null) {
      this.bgColor = this.item.bgColor;
    }

    this.isIncrease = this.item.isIncrease;
    this.isCurrency = this.item.isCurrency;
    this.duration = this.item.duration;
    this.percentValue = this.item.percentValue;
    this.refresh = this.item.refresh == null ? 0 : this.item.refresh;
    this.query = this.item.query;
    this.refreshSeconds$.next(this.refresh);
  }

  /**
   * undefined = sem query (nada a atualizar); null = query sem linhas;
   * objeto = primeira linha do resultado.
   */
  private fetchValue$(): Observable<Record<string, unknown> | null | undefined> {
    if (this.query == null) {
      return of(undefined);
    }

    const executeQuery = this.dvs.applyFilters(this.query, this.formFilter);
    this.loading = true;

    return this.dvs.loadData(executeQuery).pipe(
      map(data => {
        const rows = data['SQL'] as Record<string, unknown>[] | null;
        return (rows == null || rows.length === 0) ? null : rows[0];
      }),
      catchError(() => {
        this.message.error(`Falha ao carregar os dados do cartão "${this.title ?? this.name}".`);
        return of(undefined);
      }),
      finalize(() => {
        this.loading = false;
      })
    );
  }

  openConfiguration(): void {
    const modalRef = openGadgetModal(this.modal, this.viewContainerRef, MiniCardConfigurationComponent, {
      item: this.item,
      name: this.name,
      // chave idDashBoard (B maiúsculo) — o creator lê exatamente esta
      idDashBoard: this.dashboard.iddashboard,
      tabName: this.tabName,
      filters: this.dashboard.filter != null ? this.dashboard.filter.map(d => [d.nome]) : null,
      type: DashBoardTypeEnum.minicard
    });

    modalRef.afterClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (value == null) {
          return;
        }
        this.item = JSON.parse(JSON.stringify(value)) as MiniCardInterface;
        this.itemChange.emit(this.item);
        this.loadVariables();
        this.manualReload$.next();
      });
  }

  excluir(): void {
    this.deleting.emit({ tabName: this.tabName, name: this.name, type: DashBoardTypeEnum.minicard });
  }

}
